import { useEffect, useRef, useState } from "react";

// Counts up from 0 to maxWhileLoading while target is not available.
// When target (number) arrives, immediately show it. If we hit the
// maxWhileLoading before target is ready, hold at that max until target arrives.
export default function useCountUp(
  target,
  { maxWhileLoading = 1000, stepWhileLoading = 10, intervalMs = 30 } = {},
) {
  const [value, setValue] = useState(0);
  const timer = useRef(null);
  const currentTargetRef = useRef(target);

  useEffect(() => {
    currentTargetRef.current = target;
    // If target arrives, snap to it immediately
    if (typeof target === "number" && target >= 0) {
      setValue(target);
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    }
  }, [target]);

  useEffect(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(() => {
      setValue((prev) => {
        const t = currentTargetRef.current;
        if (typeof t === "number" && t >= 0) {
          // Target available: snap to it and stop updating
          return t;
        }
        if (prev >= maxWhileLoading) return maxWhileLoading;
        const next = prev + stepWhileLoading;
        return next >= maxWhileLoading ? maxWhileLoading : next;
      });
    }, intervalMs);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [intervalMs, maxWhileLoading, stepWhileLoading]);

  return value;
}
